import type { SentinelPlanWarning } from 'features/sentinelDesign/types'
import { FC, ReactNode } from 'react'
import styled from 'styled-components'

const Note = styled.p`
  margin: 0.15rem 0 0;
  font-size: 0.68rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.forecast.secondaryText};
  border-left: 2px solid ${({ theme }) => theme.forecast.border};
  padding-left: 0.4rem;
`

const WarnNote = styled(Note)`
  color: #f97316;
  border-left-color: #f97316;
`

export interface PlanRationaleProps {
  /** The agent's justification for this field, when it gave one. */
  rationale?: string
  /** Warnings the backend attached to this field. */
  warnings?: SentinelPlanWarning[]
}

/**
 * Why the agent chose a field, rendered directly under that field's control,
 * plus any warning scoped to it. Advisory only — nothing here blocks a run.
 */
export const PlanRationale: FC<PlanRationaleProps> = ({
  rationale,
  warnings,
}) => {
  if (!rationale && !warnings?.length) return null
  return (
    <>
      {rationale && <Note>{rationale}</Note>}
      {warnings?.map((warning) => (
        <WarnNote key={`${warning.code ?? ''}${warning.message}`}>
          {warning.message}
        </WarnNote>
      ))}
    </>
  )
}

/**
 * Builds the per-field annotation renderer the knob panels take, pairing the
 * agent's rationale map with the warnings that name a field.
 */
export const planNoteRenderer = (
  rationale: Record<string, string> | undefined,
  warnings: SentinelPlanWarning[] | undefined
): ((field: string) => ReactNode) => {
  const byField = new Map<string, SentinelPlanWarning[]>()
  for (const warning of warnings ?? []) {
    if (!warning.field) continue
    const existing = byField.get(warning.field)
    if (existing) existing.push(warning)
    else byField.set(warning.field, [warning])
  }
  function renderPlanNote(field: string): ReactNode {
    return (
      <PlanRationale
        rationale={rationale?.[field]}
        warnings={byField.get(field)}
      />
    )
  }
  return renderPlanNote
}

/** Warnings that name no field, so they belong at the top of the plan. */
export const unscopedWarnings = (
  warnings: SentinelPlanWarning[] | undefined
): SentinelPlanWarning[] => (warnings ?? []).filter((warning) => !warning.field)
